// Page-level chrome ported from ionq-cloud-ui: ViewHeader, the two
// empty views and the Aria-1 retirement banner. Same copy, tokens and metrics as the remote so the
// ported page is visually indistinguishable.

// The dark twin is opaque on the panel it sits on (JobsTable's bg.panel,
// #1a222a) — the same backdrop the remote's JobsEmptyView declares.
import quantumJobsDark from '@assets/quantum-jobs.svg?width=300&bg=1a222a&raster=dark';
import quantumJobsLight from '@assets/quantum-jobs.svg?width=300&raster=light';
import {
    Alert,
    Box,
    Button,
    CloseButton,
    EmptyState,
    Heading,
    HStack,
    Icon,
    Image,
} from '@chakra-ui/react';
import { PAGE_MAX_WIDTH } from '@ionq/legacy-ui';
import { useTheme } from 'next-themes';
import { useState } from 'react';
import { MdOutlineSearch } from 'react-icons/md';

/** MaxWidthContainer's padding seam. `_cloudMd` is the REMOTE's condition
 * (v2's overridden `md`, 666px), composed into the shell's system via its
 * theme-config; no shell breakpoint matches it, and the shell's typegen runs
 * against legacy-ui only, hence the cast. */
export const PAGE_CONTENT_PX = { base: 4, _cloudMd: 6 } as never;
export { PAGE_MAX_WIDTH };

/**
 * @public Unmounted since the jobs page dropped its heading, kept on
 * request as the reference for a possible page-header return.
 */
export const ViewHeader = ({
    title,
    subTitle,
}: {
    title?: React.ReactNode;
    subTitle?: React.ReactNode;
}) => (
    <Box
        pt='16px'
        bg='bg.subtle'
        borderBottom='1px solid'
        borderColor='border'
        px={PAGE_CONTENT_PX}
        mb='4'
    >
        <Box maxW={PAGE_MAX_WIDTH} margin='auto'>
            <Box display='flex' flexDir='column' mb={4}>
                {/* The page's single h1 (document outline), styled like the
                    remote's title text. */}
                <Heading as='h1' textStyle='heading-3' color='fg'>
                    {title}
                </Heading>
                <Box mt='4px' textStyle='body-5'>
                    {subTitle}
                </Box>
            </Box>
        </Box>
    </Box>
);

/** The empty view's illustration, as rasterized WebP rather than the vector
 * (vite-plugin-raster.ts has why). One <img> is mounted, picked from the
 * resolved color mode — mounting both and hiding one would download both.
 * `sizes` is required with `w` descriptors; 300px is the box at every
 * viewport that matters (narrower ones only shrink it, so the browser still
 * picks a bitmap at least as wide as it draws). */
const QuantumJobsIllustration = () => {
    const { forcedTheme, resolvedTheme } = useTheme();
    const image =
        (forcedTheme ?? resolvedTheme) === 'dark'
            ? quantumJobsDark
            : quantumJobsLight;
    return (
        <Image
            src={image.src}
            srcSet={image.srcSet}
            sizes={`${image.width}px`}
            htmlWidth={image.width}
            htmlHeight={image.height}
            w={image.width}
            mb='30px'
            alt=''
        />
    );
};

export const JobsEmptyView = () => (
    <EmptyState.Root maxW='458px' ml='auto' mr='auto' mb='40px' px='0' py='0'>
        <EmptyState.Content gap='0.5rem'>
            <EmptyState.Title
                textStyle='heading-2'
                color='fg'
                textAlign='center'
                mb='20px'
                mt='40px'
            >
                All of your quantum jobs in one place
            </EmptyState.Title>
            <QuantumJobsIllustration />
            <EmptyState.Description
                textStyle='body-3'
                color='fg'
                textAlign='center'
                mb='30px'
            >
                The cloud console shows you the details of the jobs submitted to
                the IonQ Quantum Cloud to this particular project, but it looks
                like you haven’t run any yet!
            </EmptyState.Description>
            {/* The brand button (fill + per-mode hover from the recipe);
                the inline props keep the donor's 8x12 / 5px / body-2 box. */}
            <Button
                asChild
                variant='brand'
                h='auto'
                py='8px'
                px='12px'
                borderRadius='5px'
                textStyle='body-2'
            >
                <a
                    href='https://docs.ionq.com/guides/sdks/qiskit'
                    target='_blank'
                    rel='noopener'
                >
                    Run your first quantum job
                </a>
            </Button>
        </EmptyState.Content>
    </EmptyState.Root>
);

export const EmptyFilterView = () => (
    <EmptyState.Root px='40px' py='40px'>
        <EmptyState.Content gap='0.5rem'>
            <EmptyState.Indicator color='fg.muted' fontSize='24px'>
                <Icon as={MdOutlineSearch} boxSize='24px' color='fg.muted' />
            </EmptyState.Indicator>
            <EmptyState.Title textStyle='body-2'>
                No jobs exist with your filter criteria
            </EmptyState.Title>
            <EmptyState.Description textStyle='body-3' color='inherit'>
                Try a different filter criteria.
            </EmptyState.Description>
        </EmptyState.Content>
    </EmptyState.Root>
);

const HIDE_ARIA1_BANNER = '__hide_aria1_retirement_banner__';

/** Warning banner for orgs still carrying the retired qpu.aria-1 target,
 * dismissed persistently per browser (same localStorage key as the remote,
 * so a dismissal there holds here). */
export const Aria1RetirementBanner = ({
    allowExtraTargets,
}: {
    allowExtraTargets?: string[] | null;
}) => {
    const [bannerHidden, setBannerHidden] = useState(
        () => localStorage.getItem(HIDE_ARIA1_BANNER) === 'yes',
    );

    if (!allowExtraTargets?.includes('qpu.aria-1') || bannerHidden) {
        return null;
    }

    // Edge-to-edge strip: the v3 Alert card radius reads wrong here (donor
    // parity — the remote's banner squares it the same way).
    return (
        <Alert.Root
            // Stock warning alert, no color overrides: both modes render
            // exactly the v3 default look (the donor's custom uiLightRed /
            // red icon strip predates the dark theme and had no dark
            // answer).
            status='warning'
            mb='0'
            borderRadius='0'
        >
            <HStack width='100%' justify='space-between' align='flex-start'>
                <HStack align='flex-start'>
                    <Alert.Indicator />
                    <Box>
                        <Alert.Title>Aria-1 has been retired.</Alert.Title>
                        <Alert.Description>
                            Please contact support@ionq.com if you have any
                            questions.
                        </Alert.Description>
                    </Box>
                </HStack>
                <CloseButton
                    size='sm'
                    onClick={() => {
                        localStorage.setItem(HIDE_ARIA1_BANNER, 'yes');
                        setBannerHidden(true);
                    }}
                />
            </HStack>
        </Alert.Root>
    );
};
